import { Header, Footer } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Mail, Phone, Clock, ShieldCheck, FileText, MessageCircle, Scale, ChevronRight } from "lucide-react";

const SECTIONS = [
  { id: "purpose", label: "Purpose" },
  { id: "scope", label: "Scope" },
  { id: "non-retaliation", label: "Non-Retaliation" },
  { id: "confidentiality", label: "Confidentiality" },
  { id: "step-1", label: "Step 1 · Informal" },
  { id: "step-2", label: "Step 2 · Formal Complaint" },
  { id: "step-3", label: "Step 3 · Investigation" },
  { id: "step-4", label: "Step 4 · Appeal" },
  { id: "contact", label: "Contact" },
];

const Eyebrow = ({ children }) => (
  <span className="text-brand-gold text-xs uppercase tracking-[0.25em] font-bold">{children}</span>
);

const SectionHeading = ({ id, icon: Icon, children }) => (
  <h2 id={id} className="scroll-mt-28 flex items-center gap-3 text-2xl sm:text-3xl font-heading font-bold text-slate-900 mb-4">
    {Icon && (
      <span className="w-9 h-9 rounded-xl bg-brand-navy/5 text-brand-navy flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </span>
    )}
    {children}
  </h2>
);

const GrievancePolicy = () => (
  <div className="min-h-screen bg-white flex flex-col" data-testid="grievance-page">
    <Header />

    {/* Hero */}
    <section className="bg-gradient-to-br from-brand-navy via-brand-navy to-[#0F1B3D] text-white">
      <div className="max-w-5xl mx-auto px-6 py-16 sm:py-20">
        <Eyebrow>Participant Rights</Eyebrow>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mt-3 mb-5 tracking-tight">
          Participant Grievance Procedure
        </h1>
        <p className="text-white/80 max-w-2xl text-base sm:text-lg leading-relaxed">
          A fair, transparent process for resolving complaints or concerns related to Aspiritech
          Academy programs, instructors, or policies. Every participant has the right to be heard.
        </p>
      </div>
    </section>

    {/* Body with side-nav */}
    <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-14 grid lg:grid-cols-[220px,1fr] gap-10">
      {/* Sticky TOC */}
      <nav className="hidden lg:block self-start sticky top-24" aria-label="In this policy">
        <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400 mb-3">In this policy</p>
        <ul className="space-y-1.5 text-sm">
          {SECTIONS.map(s => (
            <li key={s.id}>
              <a href={`#${s.id}`} className="group flex items-center gap-1.5 text-slate-600 hover:text-brand-navy transition" data-testid={`toc-${s.id}`}>
                <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 text-brand-gold transition" />
                <span className="group-hover:font-medium">{s.label}</span>
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <article className="prose prose-slate max-w-none">
        {/* Purpose */}
        <SectionHeading id="purpose" icon={ShieldCheck}>Purpose</SectionHeading>
        <p className="text-slate-600 leading-relaxed">
          The purpose of this grievance procedure is to provide a fair and transparent process for
          resolving complaints or concerns related to Aspiritech Academy programs, instructors, or
          policies. This procedure ensures that all participants have the opportunity to voice
          concerns and seek resolution in a timely and respectful manner.
        </p>

        {/* Scope */}
        <SectionHeading id="scope" icon={Scale}>Scope</SectionHeading>
        <p className="text-slate-600 leading-relaxed">
          This grievance procedure applies to all participants enrolled in Aspiritech Academy
          programs and addresses concerns related to program delivery, instructor conduct, policies,
          or any matter where a participant believes Aspiritech Academy or its personnel has acted
          in a manner that violates federal or state laws, regulations, or organizational policies.
        </p>

        {/* Non-Retaliation */}
        <SectionHeading id="non-retaliation" icon={ShieldCheck}>Non-Retaliation Policy</SectionHeading>
        <Card className="bg-amber-50/60 border-amber-200">
          <CardContent className="pt-5 pb-5">
            <p className="text-slate-700 leading-relaxed m-0">
              Aspiritech Academy is committed to protecting the rights of participants who file
              grievances. <strong>No participant shall be threatened, coerced, or discriminated
              against for filing a complaint or participating in the grievance process.</strong>
            </p>
          </CardContent>
        </Card>

        {/* Confidentiality */}
        <SectionHeading id="confidentiality" icon={ShieldCheck}>Confidentiality</SectionHeading>
        <p className="text-slate-600 leading-relaxed">
          Aspiritech Academy will maintain confidentiality to the extent possible during the
          investigation and resolution process. Information will only be shared with individuals
          who have a legitimate need to know in order to investigate and resolve the grievance.
        </p>

        {/* Step 1 */}
        <SectionHeading id="step-1" icon={MessageCircle}>Step 1 · Informal Resolution</SectionHeading>
        <p className="text-slate-600 leading-relaxed">
          <strong>Direct Communication.</strong> Participants are encouraged to resolve concerns informally
          whenever possible. Most grievances can be resolved through respectful, direct communication
          with the instructor or staff member involved.
        </p>
        <Card className="card-surface">
          <CardContent className="pt-5 pb-5 grid sm:grid-cols-3 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> Timeline
              </p>
              <p className="text-sm text-slate-700">Attempt resolution within <strong>5–10 business days</strong> of the incident.</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Method</p>
              <p className="text-sm text-slate-700">A respectful in-person meeting, phone call, video call, or email correspondence.</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Purpose</p>
              <p className="text-sm text-slate-700">Most conflicts are best resolved at this level through open dialogue and mutual understanding.</p>
            </div>
          </CardContent>
        </Card>

        {/* Step 2 */}
        <SectionHeading id="step-2" icon={FileText}>Step 2 · Formal Written Complaint</SectionHeading>
        <p className="text-slate-600 leading-relaxed">
          If the matter is not resolved informally, the participant may submit a formal written complaint.
        </p>
        <Card className="border-brand-navy/20 bg-brand-navy/5">
          <CardContent className="pt-5 pb-5">
            <p className="text-[10px] uppercase tracking-wider font-bold text-brand-navy mb-2">How to File</p>
            <p className="text-slate-700 m-0">Submit your written complaint via email to:</p>
            <p className="mt-2 mb-1">
              <a href="mailto:tanika.edmonds@aspiritech.org" className="text-brand-navy font-bold hover:underline">
                tanika.edmonds@aspiritech.org
              </a>
            </p>
            <p className="text-sm text-slate-600">
              Please include <a href="mailto:tara.may@aspiritech.org" className="text-brand-navy font-medium hover:underline">tara.may@aspiritech.org</a> in copy.
            </p>
          </CardContent>
        </Card>

        <h3 className="text-lg font-heading font-bold text-slate-900 mt-8 mb-3">Required Information</h3>
        <p className="text-slate-600 leading-relaxed">
          A properly filed written complaint must include the following information, to the extent known:
        </p>
        <ol className="list-decimal pl-6 space-y-1.5 text-slate-600">
          <li>Name and contact information of the person filing the complaint</li>
          <li>Program in which the participant is enrolled</li>
          <li>Date(s) of the alleged incident or concern</li>
          <li>Name(s) of the instructor(s) or staff member(s) involved, if applicable</li>
          <li>A written statement detailing the specific concerns or allegations</li>
          <li>Names and contact information of any witnesses, if known</li>
          <li>Any supporting documentation or other evidence, if available</li>
          <li>A clear statement of the resolution or outcome sought by the participant</li>
        </ol>
        <h3 className="text-lg font-heading font-bold text-slate-900 mt-8 mb-3">Time Limitations</h3>
        <p className="text-slate-600 leading-relaxed">
          Formal grievances should be submitted within <strong>20–30 business days</strong> of the
          incident or concern. Grievances submitted after this timeframe may still be considered on
          a case-by-case basis, depending on the circumstances.
        </p>

        {/* Step 3 */}
        <SectionHeading id="step-3" icon={ShieldCheck}>Step 3 · Investigation and Resolution</SectionHeading>
        <p className="text-slate-600 leading-relaxed">
          Upon receipt of a formal written complaint, Aspiritech Academy will:
        </p>
        <ol className="list-decimal pl-6 space-y-1.5 text-slate-600">
          <li>Acknowledge receipt of the complaint within <strong>3 business days</strong>.</li>
          <li>Conduct an investigation by gathering relevant information, interviewing involved parties, and reviewing documentation as needed.</li>
          <li>Provide a written response outlining findings and resolution steps within <strong>10–15 business days</strong> of receiving the complaint.</li>
        </ol>
        <p className="text-sm text-slate-500 leading-relaxed mt-3">
          Complex cases may require additional time; participants will be notified if an extension is needed.
        </p>

        {/* Step 4 */}
        <SectionHeading id="step-4" icon={Scale}>Step 4 · Appeal Process</SectionHeading>
        <p className="text-slate-600 leading-relaxed">
          If the participant is not satisfied with the resolution, they may submit an appeal within
          <strong> 10 business days</strong> of receiving the written response.
        </p>
        <Card className="border-brand-navy/20 bg-brand-navy/5">
          <CardContent className="pt-5 pb-5">
            <p className="text-[10px] uppercase tracking-wider font-bold text-brand-navy mb-2">Submit Appeals To</p>
            <p className="mb-1">
              <a href="mailto:tara.may@aspiritech.org" className="text-brand-navy font-bold hover:underline">
                tara.may@aspiritech.org
              </a>
              <span className="text-slate-500"> (CEO, Aspiritech)</span>
            </p>
            <p className="text-sm text-slate-600 m-0">
              The CEO or designated leadership will review the appeal and provide a final written
              decision within <strong>10–15 business days</strong>.
            </p>
          </CardContent>
        </Card>

        {/* Contact */}
        <SectionHeading id="contact" icon={Mail}>Contact Information</SectionHeading>
        <p className="text-slate-600 leading-relaxed">
          For questions or assistance with the grievance process, please contact:
        </p>
        <Card className="card-surface">
          <CardContent className="pt-6 pb-6">
            <p className="font-heading text-lg font-bold text-slate-900">Tanika Edmonds</p>
            <p className="text-sm text-slate-500 mb-3">Vice President of Strategic Planning</p>
            <div className="space-y-1.5 text-sm">
              <p className="flex items-center gap-2 text-slate-700">
                <Mail className="w-4 h-4 text-brand-navy shrink-0" />
                <a href="mailto:tanika.edmonds@aspiritech.org" className="text-brand-navy hover:underline">tanika.edmonds@aspiritech.org</a>
              </p>
              <p className="flex items-center gap-2 text-slate-700">
                <span className="w-4 h-4 inline-flex items-center justify-center text-brand-navy" aria-hidden="true">🌐</span>
                <a href="https://aspiritech.org/aspiritech-academy/" target="_blank" rel="noreferrer noopener" className="text-brand-navy hover:underline">aspiritech.org/aspiritech-academy</a>
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-slate-400 mt-10 italic">
          Aspiritech Academy — a 90% autistic tech company that also trains autistic adults for employment.
        </p>
      </article>
    </main>

    <Footer />
  </div>
);

export default GrievancePolicy;
